import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs';
import { BaseServiceFacade } from 'src/app/common/store/types';
import { AppState } from '../types';
import { navigationMemoryActions } from './navigation-memory.actions';
import { sameRoute } from './navigation-memory.reducer';
import { navigationMemorySelectors } from './navigation-memory.selectors';
import { INavigationMemoryState } from './types';

/**
 * Reusable navigation-memory facade. Lets any screen remember local state
 * across a navigation round-trip (e.g. a drill-down then a direct return):
 *  - `keep(...)` arms the memory when leaving,
 *  - `getMemory()` + `isValid(...)` restore it on a direct return,
 *  - `reset()` clears it.
 *
 * Hop tracking is driven automatically from router navigations, so target pages
 * need no wiring. A "direct return" is one where the user came back to the
 * origin without an off-path navigation (`hop < 1`).
 */
@Injectable()
export class NavigationMemoryService extends BaseServiceFacade {
  constructor(store: Store<AppState>, router: Router) {
    super(store);
    router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) =>
        this.store.dispatch(
          navigationMemoryActions.updateHop({ url: e.urlAfterRedirects }),
        ),
      );
  }

  keep = <T>(
    key: string,
    payload: T,
    urlSource: string,
    urlTarget: string,
  ): void => {
    this.store.dispatch(
      navigationMemoryActions.keep({ key, payload, urlSource, urlTarget }),
    );
  };

  getMemory = () => this.store.select(navigationMemorySelectors.getMemory);

  reset = (): void => {
    this.store.dispatch(navigationMemoryActions.reset());
  };

  /** A memory is restorable only on a direct return to the same origin. */
  isValid = (
    memory: INavigationMemoryState,
    key: string,
    urlSource: string,
  ): boolean =>
    memory.key === key &&
    sameRoute(memory.urlSource, urlSource) &&
    memory.hop < 1;
}
